/* Version: 3.5.137 */
'use client';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { useState } from 'react';
import { ButtonCheckmarkIcon, Cross } from '@manulife/mux-cds-icons';
import PropTypes from 'prop-types';
import Icon from '../Icon/Icon.js';
import i18n from '../../lang/i18n/index.js';
import { safeRest, rem } from '../../utils/functions.js';
import { MESSAGE_VARIANT } from './constants.js';
import { MessageCardWrapper, IconBar, IconCircle, CloseButton, MessageTitle, SecondaryIcon } from './styledComponents.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import useTheme from '../../hooks/useTheme.js';
import useLang from '../../hooks/useLang.js';

var _excluded = ["id", "variant", "titleText", "titleAs", "children", "icon", "closeIcon", "hasCloseButton", "closeIconAriaLabel"];
function getDefaultIcon(variant, theme) {
  switch (variant) {
    case MESSAGE_VARIANT.PRIMARY_WARNING:
    case MESSAGE_VARIANT.PRIMARY_ALERT:
    case MESSAGE_VARIANT.HARDSTOP:
      return /*#__PURE__*/jsx(Icon, {
        manulifeName: "alert",
        fill: theme.colorPalette.neutralColors.light.shade_white
      });
    case MESSAGE_VARIANT.SECONDARY_WARNING:
    case MESSAGE_VARIANT.SECONDARY_ALERT:
      return /*#__PURE__*/jsx(SecondaryIcon, {
        manulifeName: "alert",
        fill: theme.colorPalette.neutralColors.light.shade_white,
        width: rem(15),
        height: rem(15)
      });
    case MESSAGE_VARIANT.PRIMARY_COMPLETE:
      return /*#__PURE__*/jsx(Icon, {
        manulifeName: "checkmark",
        fill: theme.colorPalette.neutralColors.light.shade_white,
        width: rem(16),
        height: rem(16)
      });
    case MESSAGE_VARIANT.SECONDARY_COMPLETE:
      return /*#__PURE__*/jsx(ButtonCheckmarkIcon, {
        color: theme.colorPalette.neutralColors.light.shade_white,
        fontSize: rem(36),
        style: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          width: '100%'
        }
      });
    case MESSAGE_VARIANT.SECONDARY_INFORMATION:
      return /*#__PURE__*/jsx(SecondaryIcon, {
        manulifeName: "info",
        fill: theme.colorPalette.neutralColors.light.shade_white,
        width: rem(15),
        height: rem(15)
      });
    default:
      return /*#__PURE__*/jsx(Icon, {
        manulifeName: "info",
        fill: theme.colorPalette.neutralColors.light.shade_white
      });
  }
}
var MessageCard = function MessageCard(_ref) {
  var id = _ref.id,
    _ref$variant = _ref.variant,
    variant = _ref$variant === undefined ? MESSAGE_VARIANT.PRIMARY_INFORMATION : _ref$variant,
    _ref$titleText = _ref.titleText,
    titleText = _ref$titleText === undefined ? '' : _ref$titleText,
    _ref$titleAs = _ref.titleAs,
    titleAs = _ref$titleAs === undefined ? 'strong' : _ref$titleAs,
    _ref$children = _ref.children,
    children = _ref$children === undefined ? '' : _ref$children,
    icon = _ref.icon,
    _ref$closeIcon = _ref.closeIcon,
    closeIcon = _ref$closeIcon === undefined ? /*#__PURE__*/jsx(Cross, {
      fontSize: rem(24)
    }) : _ref$closeIcon,
    _ref$hasCloseButton = _ref.hasCloseButton,
    hasCloseButton = _ref$hasCloseButton === undefined ? false : _ref$hasCloseButton,
    _ref$closeIconAriaLab = _ref.closeIconAriaLabel,
    closeIconAriaLabel = _ref$closeIconAriaLab === undefined ? '' : _ref$closeIconAriaLab,
    rest = _objectWithoutProperties(_ref, _excluded);
  var theme = useTheme();
  var safeProps = safeRest(rest);
  var _useState = useState(true),
    _useState2 = _slicedToArray(_useState, 2),
    isOpen = _useState2[0],
    setIsOpen = _useState2[1];
  var lang = useLang();
  var primaryVariants = [MESSAGE_VARIANT.PRIMARY_INFORMATION, MESSAGE_VARIANT.PRIMARY_ALERT, MESSAGE_VARIANT.PRIMARY_WARNING, MESSAGE_VARIANT.PRIMARY_COMPLETE];
  return /*#__PURE__*/jsxs(MessageCardWrapper, _objectSpread(_objectSpread({
    id: id,
    variant: variant,
    isOpen: isOpen,
    theme: theme,
    "data-testid": "messagecard-wrapper",
    hasCloseButton: hasCloseButton
  }, safeProps), {}, {
    children: [primaryVariants.indexOf(variant) !== -1 || variant === MESSAGE_VARIANT.HARDSTOP ? /*#__PURE__*/jsx(IconBar, {
      "data-testid": "icon-wrapper",
      "aria-hidden": true,
      variant: variant,
      theme: theme,
      children: icon || getDefaultIcon(variant, theme)
    }) : /*#__PURE__*/jsx(IconCircle, {
      "data-testid": "icon-wrapper",
      "aria-hidden": true,
      variant: variant,
      theme: theme,
      children: icon || getDefaultIcon(variant, theme)
    }), hasCloseButton && primaryVariants.indexOf(variant) !== -1 && /*#__PURE__*/jsx(CloseButton, {
      "aria-label": closeIconAriaLabel || i18n[lang].messageCard.closeIconAriaLabelDefault,
      onClick: function onClick() {
        return setIsOpen(false);
      },
      children: closeIcon
    }), /*#__PURE__*/jsx(MessageTitle, {
      as: titleAs,
      children: titleText
    }), /*#__PURE__*/jsx("div", {
      children: children
    })]
  }));
};
process.env.NODE_ENV !== "production" ? MessageCard.propTypes = {
  /** ID is required for Automated Testing, Google analytics and Google Tag Manager */
  id: PropTypes.string,
  /** Which variant of the message component to display */
  variant: PropTypes.oneOf([MESSAGE_VARIANT.PRIMARY_INFORMATION, MESSAGE_VARIANT.PRIMARY_WARNING, MESSAGE_VARIANT.PRIMARY_ALERT, MESSAGE_VARIANT.PRIMARY_COMPLETE, MESSAGE_VARIANT.SECONDARY_INFORMATION, MESSAGE_VARIANT.SECONDARY_WARNING, MESSAGE_VARIANT.SECONDARY_ALERT, MESSAGE_VARIANT.SECONDARY_COMPLETE, MESSAGE_VARIANT.HARDSTOP]),
  /** Title of the message */
  titleText: PropTypes.string,
  /** Component used when rendering the title */
  titleAs: PropTypes.elementType,
  /** Body of the message */
  children: PropTypes.node,
  /**
   * Icon displayed left of message. Only specify if you need something different from the branding guide.
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  icon: PropTypes.node,
  /**
   * Flag to show closeIcon and allow the message to be dismissed.
   *
   * This prop only works with the PRIMARY variant.
   * */
  hasCloseButton: PropTypes.bool,
  /**
   * Icon used as the close button. Only specify if you need something different from the branding guide,
   * or if you need to specify the id (a fixed id is required for automated testing)
   *
   * Refer to our documentation on how to pass one of the following icons to this prop:
   *
   * [CDS icons](/docs/icons) /
   * [Manulife icons](/docs/icons#manulife-icons) /
   * [Custom icons](/docs/icons#custom-icons).
   * */
  closeIcon: PropTypes.node,
  /** Close icon aria-label for AODA, web accessibility. Default value depends on language. */
  closeIconAriaLabel: PropTypes.string
} : undefined;
process.env.NODE_ENV !== "production" ? MessageCard.displayName = 'MessageCard' : undefined;

export { MessageCard as default };
